import styles from './CardUltimosPedidos.module.css';
import { useContext } from 'react';
import { FiCreditCard, FiDollarSign } from 'react-icons/fi';
import { ItensPedidosContext } from '../contexts/ItensPedidosContext';
import { ClientesContext } from '../contexts/ClientesContext';
import { numeroExibicaoPedido } from '../utils/pedido';

const CORES_STATUS = {
  pendente: '#c7222a',
  'em andamento': '#c9861f',
  pronto: '#2f8f5b',
  entregue: '#6b7280',
  cancelado: '#9ca3af',
  desconhecido: 'transparent',
};

function tempoDecorridoDesde(data) {
  const minutos = Math.max(0, Math.round((Date.now() - new Date(data).getTime()) / 60000));
  if (minutos < 1) return 'agora mesmo';
  if (minutos < 60) return `há ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  return `há ${horas}h${resto ? ` ${resto}min` : ''}`;
}

const ROTULO_TIPO_IFOOD = { DELIVERY: 'Entrega', TAKEOUT: 'Retirada', DINE_IN: 'No local', INDOOR: 'No local' };

// Item que veio do iFood: guarda nome/observação/opções do jeito que o iFood mandou
// (ver models/ItensDoPedido.js). Itens dos outros canais não têm isso.
function dadosIfoodDoItem(item) {
  return item?.dados_externos && item.dados_externos.origem === 'ifood' ? item.dados_externos : null;
}

function OpcoesDoItem({ opcoes }) {
  if (!Array.isArray(opcoes) || opcoes.length === 0) return null;
  return (
    <ul className={styles.itemOpcoes}>
      {opcoes.map((o, i) => (
        <li key={i}>
          + {o.quantidade > 1 ? `${o.quantidade}× ` : ''}{o.nome}
          {o.grupo ? <small> ({o.grupo})</small> : null}
          {Array.isArray(o.customizacoes) && o.customizacoes.length > 0 && (
            <ul className={styles.itemOpcoesSub}>
              {o.customizacoes.map((c, j) => (
                <li key={j}>{c.quantidade > 1 ? `${c.quantidade}× ` : ''}{c.nome}</li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
}

function MetaPagamento({ formaPagamento }) {
  const forma = (formaPagamento || '').trim().toLowerCase();
  if (forma === 'pix') return <span className={styles.iconePix}>PIX</span>;
  const Icone = forma === 'dinheiro' ? FiDollarSign : FiCreditCard;
  return (
    <span className={styles.metaPagamento}>
      <Icone size={13} /> {formaPagamento}
    </span>
  );
}

function CardUltimosPedidos({ pedido, exibirNomes = true, mostrarBotoesStatus = true, typeView, mudarStatusPedido, mostrarClienteEStatus = true, exibirMeta = false, statusAvancoPermitido = null }) {
  const { itens_do_pedido } = useContext(ItensPedidosContext);
  const { clientes } = useContext(ClientesContext);
  const API_URL = process.env.REACT_APP_API_URL;

  // Normaliza o status do pedido
  let statusNormalized = pedido.status?.trim().toLowerCase();
  if (!['pronto', 'entregue', 'pendente', 'em andamento', 'cancelado'].includes(statusNormalized)) {
    statusNormalized = 'desconhecido';
  }

  // Ordem dos status e próximos status possíveis
  const statusOrdem = ["pendente", "em andamento", "pronto", "entregue"];
  const indexAtual = statusOrdem.indexOf(statusNormalized);
  let statusPossiveis = indexAtual >= 0 && indexAtual < statusOrdem.length - 1
    ? [statusOrdem[indexAtual + 1]]
    : [];

  // Restringe quais avanços aparecem nesta tela (ex: Tela da Cozinha só
  // marca "em andamento"/"pronto" — quem entrega ao cliente é outra etapa).
  if (statusAvancoPermitido) {
    statusPossiveis = statusPossiveis.filter((status) => statusAvancoPermitido.includes(status));
  }

  const statusLabel = {
    pronto: 'Pronto',
    entregue: 'Entregue',
    pendente: 'Pendente',
    'em andamento': 'Em andamento',
    cancelado: 'Cancelado'
  };

  const cancelarPedido = async () => {
    if (!window.confirm('Deseja cancelar este pedido?')) return;
    try {
      const response = await fetch(
        `${API_URL}/pedidos/${pedido.id_pedido}/status`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({ status: "cancelado" }),
        }
      );
      if (!response.ok) throw new Error("Erro ao cancelar pedido");
      await mudarStatusPedido(pedido.id_pedido, "cancelado");
    } catch (error) {
      console.error(error);
    }
  };

  const itensDoPedido = Array.isArray(itens_do_pedido)
    ? itens_do_pedido.filter(item => Number(item.id_pedido) === Number(pedido.id_pedido))
    : [];

  const nomeCliente = pedido.cliente?.nome ||
    (Array.isArray(clientes)
      ? clientes.find(c => Number(c.id_cliente) === Number(pedido.id_cliente))?.nome
      : null) ||
    pedido.nome_cliente ||
    'Cliente';

  const totalPedido = itensDoPedido.reduce((acc, item) => {
    const preco = item.produto?.preco || item.total_item / item.quantidade || 0;
    return acc + preco * item.quantidade;
  }, 0);

  const statusClass = statusNormalized.replace(/\s+/g, '');
  const pedidoDate = new Date(pedido.data_pedido);
  const dataFormatada = pedidoDate.toLocaleDateString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric'
  });
  const horarioFormatado = pedidoDate.toLocaleTimeString('pt-BR', {
    hour: '2-digit', minute: '2-digit'
  });

  const isCancelado = statusNormalized === 'cancelado';
  // Tela da Cozinha: card compacto, só com o essencial pra bater o olho e preparar.
  const modoCozinha = typeView === 'cozinha';

  return (
    <div className={`${styles.cardPrincipal} ${isCancelado ? styles.cardCancelado : ''} ${exibirMeta ? styles.cardComMeta : ''} ${modoCozinha ? styles.cardCozinha : ''}`}>
      {exibirMeta && (
        <span className={styles.urgentBar} style={{ background: CORES_STATUS[statusNormalized] }} />
      )}

      {/* Cabeçalho */}
      <div className={styles.cabecalhoCard}>
        <div className={styles.cabecalhoEsquerda}>
          <p className={styles.txtIDPedido}>
            Pedido #{numeroExibicaoPedido(pedido)}
            {pedido.origem_pedido === 'ifood' && (
              <>
                <span className={styles.badgeIfood}>iFood{pedido.ifood_display_id ? ` ${pedido.ifood_display_id}` : ''}</span>
                {ROTULO_TIPO_IFOOD[pedido.ifood_order_type] && (
                  <span className={styles.tagTipoIfood}>{ROTULO_TIPO_IFOOD[pedido.ifood_order_type]}</span>
                )}
              </>
            )}
          </p>
          {!modoCozinha && (
            <p className={styles.dataHora}>{dataFormatada} · {horarioFormatado}</p>
          )}
          {exibirMeta && (
            <p className={styles.metaLinha}>
              <span style={{ color: CORES_STATUS[statusNormalized], fontWeight: 700 }}>
                {tempoDecorridoDesde(pedido.data_pedido)}
              </span>
              {pedido.forma_de_pagamento && (
                <>
                  <span className={styles.metaSep}>·</span>
                  <MetaPagamento formaPagamento={pedido.forma_de_pagamento} />
                </>
              )}
              {pedido.troco_para && (
                <>
                  <span className={styles.metaSep}>·</span>
                  <span>Troco p/ R$ {Number(pedido.troco_para).toFixed(2)}</span>
                </>
              )}
            </p>
          )}
          {mostrarClienteEStatus && (
            <p className={styles.nomeCliente}>{nomeCliente}</p>
          )}
        </div>
        {mostrarClienteEStatus && (
          <span className={`${styles.badgeStatus} ${styles[statusClass]}`}>
            {statusLabel[statusNormalized] || statusNormalized}
          </span>
        )}
      </div>

      {/* Botão cancelar — abaixo do status. Fora da Tela da Cozinha (somente
          leitura por enquanto), já que ela não passa mudarStatusPedido. */}
      {pedido.status === "pendente" && typeView !== "cozinha" && (typeView === "cliente" || pedido.origem_pedido === 'balcão' || pedido.origem_pedido === 'garcom') && (
        <button className={styles.btnCancelar} onClick={cancelarPedido}>
          ✕ Cancelar pedido
        </button>
      )}

      {/* Itens */}
      {exibirNomes && itensDoPedido.length > 0 && (
        <div className={styles.itens}>
          {itensDoPedido.map(item => {
            const ifood = dadosIfoodDoItem(item);
            const observacao = ifood?.observacoes || item.observacao;
            return (
              <div key={item.id_item} className={styles.itemCard}>
                <div className={styles.itemCardLinha}>
                  <span className={styles.quantidade}>{item.quantidade}×</span>
                  <span className={styles.nomeProduto}>{item.produto?.nome_item || item.nome_item_externo || 'Produto'}</span>
                </div>
                {ifood && <OpcoesDoItem opcoes={ifood.opcoes} />}
                {/* iFood e WhatsApp: a observação do cliente ("sem cebola") aparece também na cozinha; nos demais canais segue só fora dela, como antes */}
                {(ifood || pedido.origem_pedido === 'whatsapp') && observacao && (
                  <p className={styles.itemObservacaoCozinha}>Obs: {observacao}</p>
                )}
                {!ifood && pedido.origem_pedido !== 'whatsapp' && !modoCozinha && item.observacao && (
                  <p className={styles.itemObservacao}>Obs: {item.observacao}</p>
                )}
                {ifood && !modoCozinha && !item.id_produto && (
                  <span className={styles.itemSemVinculo}>⚠ sem vínculo com produto (cód. iFood {item.codigo_externo || '—'})</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Rodapé: total (não faz sentido na cozinha, que só prepara os itens) */}
      {!modoCozinha && (
        <div className={styles.rodape}>
          <span className={styles.totalLabel}>Total</span>
          <span className={`${styles.totalValor} ${isCancelado ? styles.totalCancelado : ''}`}>
            R$ {(pedido.origem_pedido === 'ifood' ? Number(pedido.total || 0) : totalPedido).toFixed(2)}
          </span>
        </div>
      )}


      {/* Botões de avanço de status (equipe interna) */}
      {mostrarBotoesStatus && typeView !== "cliente" && statusPossiveis.length > 0 && (
        <div className={styles.botoesStatus}>
          {statusPossiveis.map(status => (
            <button
              key={status}
              className={`${styles.botaoStatus} ${styles[status.replace(/\s+/g,'')]}`}
              onClick={() => mudarStatusPedido(pedido.id_pedido, status)}
            >
              {statusLabel[status]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default CardUltimosPedidos;
